import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../auth/UserContext';
import { downloadReportForUser } from '../utils/scoreReport';
import * as api from '../api/client';

interface TerritoryInfo {
  name: string;
  directorId: string | null;
  directorName: string | null;
  userCount: number;
  teamCount: number;
  unassignedCount: number;
}

interface UserScore {
  userId: string;
  name: string;
  teamId?: string;
  overall: number;
  factors: { key: string; label: string; ratio: number }[];
  totalIssues: number;
  error?: string;
}

interface TeamAverage {
  teamId: string;
  teamName: string;
  teamLeadName: string | null;
  average: number;
  memberCount: number;
  top: UserScore | null;
  bottom: UserScore | null;
}

interface TrendPoint {
  date: string;      // YYYY-MM-DD
  average: number;   // 0..1
  issueCount: number;
}

interface TerritoryScoreData {
  scores: UserScore[];
  average: number;
  top: UserScore | null;
  bottom: UserScore | null;
  teamAverages: TeamAverage[];
  trend?: TrendPoint[];
}

/**
 * 30-day score trend for the active territory. One series, so no legend — the
 * heading names it. Points come from the daily snapshot capture; scores are
 * computed live and can't be backfilled, so this is empty until captures run.
 */
function TerritoryTrend({ trend, territory }: { trend: TrendPoint[] | undefined; territory: string }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const points = trend || [];

  if (points.length === 0) {
    return (
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header">30-day trend</div>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '10px 0 0' }}>
          No history yet — scores are captured once a day from now on, so the line builds up over the coming days.
        </p>
      </div>
    );
  }

  const W = 720, H = 190;
  const PAD = { t: 14, r: 18, b: 26, l: 38 };
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;
  const x = (i: number) => PAD.l + (points.length === 1 ? plotW / 2 : (i * plotW) / (points.length - 1));
  const y = (v: number) => PAD.t + (1 - Math.max(0, Math.min(1, v))) * plotH;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.average).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${(PAD.t + plotH).toFixed(1)} L${x(0).toFixed(1)},${(PAD.t + plotH).toFixed(1)} Z`;

  const last = points[points.length - 1];
  const first = points[0];
  const changePts = Math.round((last.average - first.average) * 1000) / 10;
  const shortDate = (d: string) => {
    const [, m, day] = d.split('-');
    return `${Number(day)}/${Number(m)}`;
  };

  const active = hoverIdx !== null ? points[hoverIdx] : null;

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '4px' }}>
        <div className="card-header" style={{ marginBottom: 0 }}>{territory} — 30-day trend</div>
        {points.length > 1 && (
          <span style={{ fontSize: '12px', color: changePts >= 0 ? 'var(--state-positive)' : 'var(--state-warning)' }}>
            {changePts >= 0 ? '▲' : '▼'} {Math.abs(changePts)} pts over {points.length} day{points.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHoverIdx(null)}
        onMouseMove={e => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width) * W;
          const i = Math.round(((px - PAD.l) / plotW) * (points.length - 1));
          setHoverIdx(Math.max(0, Math.min(points.length - 1, i)));
        }}
      >
        {/* Recessive gridlines + axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map(v => (
          <g key={v}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke="var(--border-card)" strokeWidth="1" />
            <text x={PAD.l - 8} y={y(v) + 4} textAnchor="end" fontSize="10" fill="var(--text-secondary)">
              {Math.round(v * 100)}%
            </text>
          </g>
        ))}

        <path d={area} fill="var(--accent-teal)" opacity="0.12" />
        <path d={line} fill="none" stroke="var(--accent-teal)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {/* Endpoint emphasised + directly labelled (not every point) */}
        <circle cx={x(points.length - 1)} cy={y(last.average)} r="4.5" fill="var(--accent-teal)" stroke="var(--bg-card)" strokeWidth="2" />
        <text x={x(points.length - 1)} y={y(last.average) - 10} textAnchor="end" fontSize="11" fontWeight="500" fill="var(--text-primary)">
          {Math.round(last.average * 100)}%
        </text>

        {/* First / last date ticks only — keeps the axis quiet */}
        <text x={PAD.l} y={H - 8} fontSize="10" fill="var(--text-secondary)">{shortDate(first.date)}</text>
        {points.length > 1 && (
          <text x={W - PAD.r} y={H - 8} textAnchor="end" fontSize="10" fill="var(--text-secondary)">{shortDate(last.date)}</text>
        )}

        {/* Hover crosshair + tooltip */}
        {active && hoverIdx !== null && (
          <g pointerEvents="none">
            <line x1={x(hoverIdx)} x2={x(hoverIdx)} y1={PAD.t} y2={PAD.t + plotH} stroke="var(--text-secondary)" strokeWidth="1" opacity="0.5" />
            <circle cx={x(hoverIdx)} cy={y(active.average)} r="5" fill="var(--accent-teal)" stroke="var(--bg-card)" strokeWidth="2" />
            <g transform={`translate(${Math.min(Math.max(x(hoverIdx), PAD.l + 52), W - PAD.r - 52)}, ${PAD.t + 8})`}>
              <rect x="-52" y="-2" width="104" height="34" rx="6" fill="var(--bg-page)" stroke="var(--border-card)" />
              <text x="0" y="12" textAnchor="middle" fontSize="10" fill="var(--text-secondary)">{shortDate(active.date)}</text>
              <text x="0" y="26" textAnchor="middle" fontSize="12" fontWeight="500" fill="var(--text-primary)">
                {Math.round(active.average * 100)}% · {active.issueCount} issues
              </text>
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}

function scoreColor(ratio: number): string {
  if (ratio >= 1.0) return 'var(--state-positive)';
  if (ratio >= 0.75) return 'var(--text-primary)';
  return 'var(--state-warning)';
}

function progressClass(ratio: number): string {
  if (ratio >= 1.0) return 'progress-fill-sage';
  if (ratio >= 0.75) return 'progress-fill-teal';
  return 'progress-fill-warning';
}

export function DirectorView() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const [territories, setTerritories] = useState<TerritoryInfo[]>([]);
  // Honour ?territory=... so links from the alert emails open the right
  // territory instead of falling back to the default (first with users).
  const [activeTerritory, setActiveTerritory] = useState<string>(searchParams.get('territory') || '');
  const [scoreData, setScoreData] = useState<TerritoryScoreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingScores, setLoadingScores] = useState(false);
  const [error, setError] = useState('');
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (userId: string) => {
    setDownloadingId(userId);
    try {
      await downloadReportForUser(userId);
    } catch {
      alert('Could not generate the report. Please try again.');
    }
    setDownloadingId(null);
  };

  const loadTerritories = useCallback(async () => {
    setLoading(true);
    try {
      const terrRes = await api.getSettingsTerritories();
      let terrs = terrRes.territories || [];

      // Directors only see their own territory; admins see all
      if (user?.role === 'director' && user.territory) {
        terrs = terrs.filter((t: TerritoryInfo) => t.name === user.territory);
      }

      setTerritories(terrs);
      // Default to the first territory with users — but only if nothing is
      // selected yet. Functional updater avoids clobbering a tab the user just
      // clicked (a stale closure here used to bounce the selection back).
      const withUsers = terrs.find((t: TerritoryInfo) => t.userCount > 0);
      if (withUsers) setActiveTerritory(prev => prev || withUsers.name);
      setError('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadTerritories(); }, [loadTerritories]);

  // Load scores for the active territory. Guarded against out-of-order
  // responses: if the user switches territory before a slow request returns,
  // the stale response is ignored instead of overwriting the new selection.
  useEffect(() => {
    if (!activeTerritory) return;
    let cancelled = false;
    setLoadingScores(true);
    setScoreData(null);
    api.getTerritoryScores(activeTerritory)
      .then(result => { if (!cancelled) setScoreData(result); })
      .catch(() => { if (!cancelled) setScoreData(null); })
      .finally(() => { if (!cancelled) setLoadingScores(false); });
    return () => { cancelled = true; };
  }, [activeTerritory]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Loading territory data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '480px', margin: '40px auto', textAlign: 'center' }}>
        <div className="warning-banner" style={{ marginBottom: '16px' }}>{error}</div>
        <button className="btn btn-primary" onClick={loadTerritories}>Retry</button>
      </div>
    );
  }

  const currentTerritory = territories.find(t => t.name === activeTerritory);
  const avg = scoreData?.average || 0;
  const sortedScores = [...(scoreData?.scores || [])].filter(s => !s.error).sort((a, b) => b.overall - a.overall);

  return (
    <div>
      <h1 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '24px' }}>Territory Overview</h1>

      {/* Territory tabs */}
      <div className="month-tabs" style={{ marginBottom: '20px', flexWrap: 'wrap' }}>
        {territories.map(t => (
          <button
            key={t.name}
            className={`month-tab${activeTerritory === t.name ? ' active' : ''}`}
            onClick={() => setActiveTerritory(t.name)}
          >
            {t.name}
            <span style={{ marginLeft: '6px', opacity: 0.6 }}>{t.userCount}</span>
          </button>
        ))}
      </div>

      {/* Territory summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        <div className="card">
          <div className="card-header">Territory Average</div>
          <div className="card-number" style={{ color: scoreColor(avg) }}>
            {loadingScores ? '...' : `${Math.round(avg * 100)}%`}
          </div>
        </div>
        <div className="card">
          <div className="card-header">Consultants</div>
          <div className="card-number">{currentTerritory?.userCount || 0}</div>
        </div>
        {scoreData?.top && (
          <div className="card">
            <div className="card-header">Top Performer</div>
            <div style={{ fontSize: '13px', fontWeight: 500, marginTop: '4px' }}>{scoreData.top.name}</div>
            <div style={{ fontSize: '18px', fontWeight: 500, color: 'var(--state-positive)' }}>{Math.round(scoreData.top.overall * 100)}%</div>
          </div>
        )}
        {scoreData?.bottom && scoreData.bottom.userId !== scoreData.top?.userId && (
          <div className="card">
            <div className="card-header">Needs Attention</div>
            <div style={{ fontSize: '13px', fontWeight: 500, marginTop: '4px' }}>{scoreData.bottom.name}</div>
            <div style={{ fontSize: '18px', fontWeight: 500, color: 'var(--state-warning)' }}>{Math.round(scoreData.bottom.overall * 100)}%</div>
          </div>
        )}
      </div>

      {/* 30-day score trend */}
      {!loadingScores && <TerritoryTrend trend={scoreData?.trend} territory={activeTerritory} />}

      {/* Director */}
      {currentTerritory?.directorName && (
        <div className="card" style={{ marginBottom: '16px', padding: '12px 20px' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Director: </span>
          <span style={{ fontWeight: 500 }}>{currentTerritory.directorName}</span>
        </div>
      )}

      {/* Team cards with averages + expandable members */}
      {(scoreData?.teamAverages || []).map(team => {
        const isExpanded = expandedTeams.has(team.teamId);
        const teamMembers = (scoreData?.scores || [])
          .filter(s => (s as any).teamId === team.teamId && !s.error)
          .sort((a, b) => b.overall - a.overall);

        return (
          <div key={team.teamId} className="card" style={{ marginBottom: '12px' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', cursor: 'pointer' }}
              onClick={() => setExpandedTeams(prev => {
                const next = new Set(prev);
                next.has(team.teamId) ? next.delete(team.teamId) : next.add(team.teamId);
                return next;
              })}
            >
              <div>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginRight: '6px' }}>{isExpanded ? '▾' : '▸'}</span>
                <span style={{ fontSize: '14px', fontWeight: 500 }}>{team.teamName}</span>
                {team.teamLeadName && (
                  <span className="pill pill-teal" style={{ marginLeft: '8px' }}>Lead: {team.teamLeadName}</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '18px', fontWeight: 500, color: scoreColor(team.average) }}>
                  {Math.round(team.average * 100)}%
                </span>
                <span className="pill pill-teal">{team.memberCount} member{team.memberCount !== 1 ? 's' : ''}</span>
              </div>
            </div>

            <div className="progress-track" style={{ marginBottom: '12px' }}>
              <div className={`progress-fill ${progressClass(team.average)}`} style={{ width: `${Math.round(team.average * 100)}%` }} />
            </div>

            {!isExpanded && (
              <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                {team.top && (
                  <span>
                    <span style={{ color: 'var(--text-secondary)' }}>Top: </span>
                    <span style={{ color: 'var(--state-positive)', fontWeight: 500 }}>{team.top.name} ({Math.round(team.top.overall * 100)}%)</span>
                  </span>
                )}
                {team.bottom && team.bottom.userId !== team.top?.userId && (
                  <span>
                    <span style={{ color: 'var(--text-secondary)' }}>Bottom: </span>
                    <span style={{ color: 'var(--state-warning)', fontWeight: 500 }}>{team.bottom.name} ({Math.round(team.bottom.overall * 100)}%)</span>
                  </span>
                )}
              </div>
            )}

            {isExpanded && teamMembers.length > 0 && (
              <table className="data-table" style={{ marginTop: '4px' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Score</th>
                    <th style={{ width: '120px' }}></th>
                    <th>Issues</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map(m => (
                    <tr key={m.userId}>
                      <td style={{ fontWeight: 500 }}>{m.name}</td>
                      <td style={{ fontWeight: 500, color: scoreColor(m.overall) }}>{Math.round(m.overall * 100)}%</td>
                      <td>
                        <div className="progress-track">
                          <div className={`progress-fill ${progressClass(m.overall)}`} style={{ width: `${Math.round(m.overall * 100)}%` }} />
                        </div>
                      </td>
                      <td>
                        <span className={`pill ${m.totalIssues === 0 ? 'pill-sage' : m.totalIssues <= 5 ? 'pill-teal' : 'pill-warning'}`}>
                          {m.totalIssues}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            className="btn btn-primary"
                            style={{ padding: '4px 10px', fontSize: '11px' }}
                            onClick={() => navigate(`/?userId=${m.userId}`)}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '11px' }}
                            disabled={downloadingId === m.userId}
                            onClick={() => handleDownload(m.userId)}
                            title="Download PDF report"
                          >
                            {downloadingId === m.userId ? '...' : '⬇ PDF'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {/* Full leaderboard */}
      {sortedScores.length > 0 && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div className="card-header" style={{ marginBottom: '12px' }}>Leaderboard — {activeTerritory}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Score</th>
                <th style={{ width: '120px' }}></th>
                <th>Issues</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedScores.map((s, idx) => (
                <tr key={s.userId}>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{idx + 1}</td>
                  <td style={{ fontWeight: 500 }}>{s.name}</td>
                  <td style={{ fontWeight: 500, color: scoreColor(s.overall) }}>{Math.round(s.overall * 100)}%</td>
                  <td>
                    <div className="progress-track">
                      <div className={`progress-fill ${progressClass(s.overall)}`} style={{ width: `${Math.round(s.overall * 100)}%` }} />
                    </div>
                  </td>
                  <td>
                    <span className={`pill ${s.totalIssues === 0 ? 'pill-sage' : s.totalIssues <= 5 ? 'pill-teal' : 'pill-warning'}`}>
                      {s.totalIssues}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        className="btn btn-primary"
                        style={{ padding: '4px 10px', fontSize: '11px' }}
                        onClick={() => navigate(`/?userId=${s.userId}`)}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '11px' }}
                        disabled={downloadingId === s.userId}
                        onClick={() => handleDownload(s.userId)}
                        title="Download PDF report"
                      >
                        {downloadingId === s.userId ? '...' : '⬇ PDF'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loadingScores && (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
          Computing scores for all consultants...
        </div>
      )}
    </div>
  );
}
